import React, { useState, useMemo } from "react";
import axios from "../api/axios";
import Cookies from "js-cookie";
import DropdownField from "../components/DropdownField";
import {
  useColleges,
  useDegrees,
  usePrograms,
  useSections,
} from "../hooks/useDropdownData";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/main.css";

// Import icons for a better UI, similar to your Examination page
import {
  FaUniversity,
  FaGraduationCap,
  FaBookOpen,
  FaUsers,
  FaIdCard,
} from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const initialFormState = {
  colleges: "",
  degree: "",
  programmes: "",
  studentIdFrom: "",
  studentIdTo: "",
  section: "",
  user: 1, // Or get from cookies if available
};

export default function UpdateStudentSection() {
  const [form, setForm] = useState(initialFormState);
  const token = Cookies.get("token");

  // Fetch dropdown data using your custom hooks
  const { colleges } = useColleges();
  const { degrees } = useDegrees();
  const { programmes } = usePrograms(); // Correctly destructure 'programmes' from the hook
  const { sections } = useSections();

  // REFACTORED: Define the form structure in a declarative way.
  // This makes it easy to add, remove, or reorder fields.
  const formSections = useMemo(
    () => [
      {
        title: "🎯 Select Target Group",
        fields: [
          {
            name: "colleges",
            label: "College",
            as: "select",
            options: colleges,
            icon: FaUniversity,
          },
          {
            name: "degree",
            label: "Degree",
            as: "select",
            options: degrees,
            icon: FaGraduationCap,
          },
          {
            name: "programmes",
            label: "Program",
            as: "select",
            options: programmes, // Pass the correct 'programmes' array to the options
            icon: FaBookOpen,
          },
        ],
      },
      {
        title: "✍️ Assign Section to Students",
        fields: [
          {
            name: "studentIdFrom",
            label: "Student ID From",
            placeholder: "Enter starting Student ID",
            icon: FaIdCard,
          },
          {
            name: "studentIdTo",
            label: "Student ID To",
            placeholder: "Enter ending Student ID",
            icon: FaIdCard,
          },
          {
            name: "section",
            label: "New Section",
            as: "select",
            options: sections,
            icon: FaUsers,
          },
        ],
      },
    ],
    [colleges, degrees, programmes, sections] // Update dependency array
  );

  // SIMPLIFIED: A single handler for all input changes.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!form.colleges || !form.degree || !form.programmes || !form.section || !form.studentIdFrom || !form.studentIdTo) {
      toast.warn("Please fill out all fields.");
      return;
    }

    const payload = {
      colleges: parseInt(form.colleges, 10),
      degree: parseInt(form.degree, 10),
      programmes: parseInt(form.programmes, 10),
      section: parseInt(form.section, 10),
      studentIdFrom: form.studentIdFrom,
      studentIdTo: form.studentIdTo,
      user: form.user,
    };

    try {
      const res = await axios.post(
        `${BASE_URL}/academic/updateStudentSection`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.status === "1") {
        toast.success("✅ Section updated successfully!");
        setForm(initialFormState); // Reset form on success
      } else {
        toast.error(`❌ ${res.data.message || "Update failed"}`);
      }
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("⚠️ Something went wrong. Please try again.");
    }
  };

  return (
    <Container className="my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold text-primary">Update Student Section</h3>
      </div>

      <Form
        onSubmit={handleSubmit}
        className="custom-form p-4 bg-white rounded shadow-sm"
      >
        {/* NEW: Dynamically render form sections and fields */}
        {formSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className={sectionIndex > 0 ? "mt-4" : ""}>
            <h5 className="mb-3 fw-semibold text-secondary">{section.title}</h5>
            <Row className="g-4">
              {section.fields.map((field) => (
                <Col md={6} lg={4} key={field.name}>
                  {field.as === "select" ? (
                    <DropdownField
                      label={field.label}
                      name={field.name}
                      value={form[field.name]}
                      options={field.options}
                      onChange={(value) =>
                        handleChange({ target: { name: field.name, value } })
                      }
                      icon={field.icon} // Pass icon to DropdownField
                    />
                  ) : (
                    <Form.Group>
                      <Form.Label className="fw-medium d-flex align-items-center">
                        {field.icon && <field.icon className="me-2" />}
                        {field.label}
                      </Form.Label>
                      <Form.Control
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                      />
                    </Form.Group>
                  )}
                </Col>
              ))}
            </Row>
          </div>
        ))}

        <div className="text-center mt-5">
          <Button type="submit" variant="primary" size="lg" className="px-5 py-2">
            Update Section
          </Button>
        </div>
      </Form>

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} />
    </Container>
  );
}